import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth, api } from '@/contexts/AuthContext';
import { LayoutDashboard, Upload, Table2, BarChart3, UserCog, Settings, ScrollText, LogOut, Phone, ChevronRight, Target, PhoneCall, AlertCircle, Map, Zap, BookOpen, Sparkles, Mail, Flame, Activity, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import '@/App.css';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/campaigns', icon: Target, label: 'Campagnes' },
  { to: '/call-mode', icon: PhoneCall, label: 'Mode Appel', highlight: true },
  { to: '/opportunites', icon: Flame, label: 'Opportunités', highlight: true },
  { to: '/rdv', icon: Calendar, label: 'RDV pris' },
  { to: '/prospects', icon: Table2, label: 'Prospects' },
  { to: '/carte', icon: Map, label: 'Carte' },
  { to: '/suggestions', icon: Sparkles, label: 'Suggestions' },
  { to: '/mon-espace', icon: Mail, label: 'Mon espace' },
  { to: '/aide', icon: BookOpen, label: 'Aide' },
];
const adminItems = [
  { to: '/imports', icon: Upload, label: 'Imports', adminOnly: true },
  { to: '/activities', icon: Activity, label: 'Activités', adminOnly: true },
  { to: '/reporting', icon: BarChart3, label: 'Reporting', adminOnly: true },
  { to: '/automatisations', icon: Zap, label: 'Automatisations' },
  { to: '/users', icon: UserCog, label: 'Utilisateurs', adminOnly: true },
  { to: '/settings', icon: Settings, label: 'Paramètres' },
  { to: '/logs', icon: ScrollText, label: 'Logs' },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [badges, setBadges] = useState({ overdue: 0, today: 0, pending: 0 });
  const [modeAppelEnabled, setModeAppelEnabled] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get('/relances/dashboard');
        const newBadges = { overdue: data.nb_en_retard || 0, today: data.nb_aujourd_hui || 0, pending: 0 };
        if (user?.role === 'super_admin' || user?.role === 'admin') {
          try {
            const { data: pending } = await api.get('/auth/pending-users');
            newBadges.pending = pending.length;
          } catch {}
        }
        setBadges(newBadges);
      } catch {}
      try {
        const { data: s } = await api.get('/settings');
        setModeAppelEnabled((s.feature_mode_appel_enabled || 'true') === 'true');
      } catch {}
    };
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [user?.role]);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const visibleNavItems = navItems.filter(i => i.to !== '/call-mode' || modeAppelEnabled);

  return (
    <div className="flex h-screen overflow-hidden bg-white" data-testid="app-layout">
      <aside className="w-64 border-r border-[#E5E7EB] bg-white flex flex-col flex-shrink-0" data-testid="sidebar">
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-[#E5E7EB]">
          <div className="w-8 h-8 rounded-lg bg-[#F97316] flex items-center justify-center">
            <Phone className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-base tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}><span className="text-[#F97316]">WASABI</span> ProspecTel</span>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <p className="text-[11px] font-semibold text-[#71717A] uppercase tracking-wider px-3 mb-2">Navigation</p>
          {visibleNavItems.map(item => (
            <NavLink key={item.to} to={item.to} end={item.to === '/'}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''} ${item.highlight && !isActive ? '!text-[#F97316] font-semibold' : ''}`}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-[18px] h-[18px]" />
              {item.label}
              {item.to === '/' && badges.overdue > 0 && (
                <Badge className="ml-auto bg-[#EF4444] text-white text-[10px] h-5 min-w-[20px] flex items-center justify-center">{badges.overdue}</Badge>
              )}
              {item.to === '/call-mode' && badges.today > 0 && (
                <Badge className="ml-auto bg-[#F97316] text-white text-[10px] h-5 min-w-[20px] flex items-center justify-center">{badges.today}</Badge>
              )}
            </NavLink>
          ))}

          {(user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'direction') && (
            <>
              <p className="text-[11px] font-semibold text-[#71717A] uppercase tracking-wider px-3 mt-5 mb-2">Administration</p>
              {adminItems.map(item => {
                if (item.adminOnly && user?.role !== 'super_admin' && user?.role !== 'admin') return null;
                return (
                  <NavLink key={item.to} to={item.to}
                    className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <item.icon className="w-[18px] h-[18px]" />
                    {item.label}
                    {item.to === '/users' && badges.pending > 0 && (
                      <Badge className="ml-auto bg-[#F97316] text-white text-[10px] h-5 min-w-[20px] flex items-center justify-center">{badges.pending}</Badge>
                    )}
                  </NavLink>
                );
              })}
            </>
          )}
        </nav>

        <div className="p-3 border-t border-[#E5E7EB]">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-[#F3F4F6] flex items-center justify-center text-xs font-semibold text-[#71717A]">
              {user?.prenom?.[0]}{user?.nom?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.prenom} {user?.nom}</p>
              <p className="text-xs text-[#71717A] capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn" className="h-8 w-8 p-0">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto bg-[#F9FAFB]">
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
